import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Download, AlertCircle, TrendingUp, TrendingDown, Target, CalendarClock, Wallet, Sparkles, Flame, Trophy } from "lucide-react";
import confetti from "canvas-confetti";
import { Navbar } from "@/components/Navbar";
import { CommandPalette } from "@/components/CommandPalette";
import { ProBanner } from "@/components/ProBanner";
import { DualTypewriter } from "@/components/DualTypewriter";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { SplitTemplatesModule } from "@/components/SplitTemplatesModule";
import { PROItemEditDrawer } from "@/components/PROItemEditDrawer";
import { useHistoryTab } from "@/hooks/useHistoryTab";
import { usePro } from "@/contexts/ProContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, CATEGORIES, formatAmount, currencySymbol } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";
import { formatDistanceToNowStrict } from "date-fns";

const CAT_COLORS = { food: "#00FF66", travel: "#00D4FF", rent: "#BF5AF2", entertainment: "#FF9500", shopping: "#FF3B30", utilities: "#FFD60A", general: "#8E8E93", subscription: "#FFD700", misc: "#64748B", health: "#EF4444", education: "#3B82F6", grocery: "#10B981", work: "#F59E0B" };
const ALL_CATS = [
  ...CATEGORIES,
  { value: "health", label: "Health", emoji: "💊" },
  { value: "subscription", label: "Subscriptions", emoji: "📱" },
  { value: "education", label: "Education", emoji: "🎓" },
  { value: "grocery", label: "Grocery", emoji: "🛒" },
  { value: "work", label: "Work", emoji: "💼" },
  { value: "misc", label: "Misc", emoji: "⚙️" },
];

export default function ProDashboard() {
  const { user } = useAuth();
  const { isPro, activatePro } = usePro();
  const { t } = useTranslation();
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [goals, setGoals] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [tab, setTab] = useHistoryTab("overview");
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(null);

  const load = async () => {
    const [s, i, e, g, r, l, st] = await Promise.all([
      api.get("/pro/summary"),
      api.get("/incomes"),
      api.get("/expenses"),
      api.get("/savings-goals"),
      api.get("/recurring-bills"),
      api.get("/pro/ledger"),
      api.get("/pro/streak").catch(() => ({ data: null })),
    ]);
    setSummary(s.data); setIncomes(i.data); setExpenses(e.data);
    setGoals(g.data); setRecurring(r.data); setLedger(l.data.items);
    setStreak(st.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const currency = summary?.currency || user?.currency || "USD";
  const sym = currencySymbol(currency);

  if (!isPro) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
        <CommandPalette open={cmdkOpen} setOpen={setCmdkOpen} />
        <ProBanner />
        <main className="max-w-3xl mx-auto px-6 sm:px-10 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FFD700]/40 bg-[#FFD700]/10 mb-8">
            <Sparkles className="h-3.5 w-3.5" style={{ color: "#FFD700" }} strokeWidth={1.5} />
            <span className="text-xs font-mono uppercase tracking-[0.25em]" style={{ color: "#FFD700" }}>{t("pro.free_during_beta")}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-6xl tracking-tighter mb-5">Unlock PRO</h1>
          <p className="text-muted-foreground text-lg mb-10 max-w-lg mx-auto">Personal finance dashboard, income & expense tracking, savings goals, recurring bills, unified payments ledger — all free while we're in beta.</p>
          <Button
            onClick={async () => { try { await activatePro(); toast.success(t("pro.activated")); } catch { toast.error("Failed"); } }}
            className="h-12 px-8 font-semibold text-black hover:opacity-90 active:scale-95 transition"
            style={{ background: "linear-gradient(135deg, #FFD700, #FFA500)" }}
            data-testid="pro-activate-cta"
          >
            <Sparkles className="h-4 w-4 mr-2" strokeWidth={2} /> {t("pro.activate")}
          </Button>
        </main>
      </div>
    );
  }

  if (loading || !summary) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
      <CommandPalette open={cmdkOpen} setOpen={setCmdkOpen} />
      <ProBanner />
      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* Hero */}
        <div className="mb-12">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">/ {t("pro_dash.title")}</div>
          <DualTypewriter prefix={t("pro_dash.hero_prefix")} suffix={t("pro_dash.hero_suffix")} />
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5 mb-5">
          <StatCard label={t("pro_dash.net_worth")} icon={Wallet} span="md:col-span-3 lg:col-span-4" gold>
            <AnimatedNumber value={summary.net_worth} currency={currency} className="text-4xl sm:text-5xl font-semibold" />
          </StatCard>
          <StatCard label={t("pro_dash.income")} icon={TrendingUp} span="md:col-span-3 lg:col-span-4">
            <div className="text-3xl font-mono font-semibold brand-text tabular-nums">+{sym}{summary.total_income.toFixed(2)}</div>
          </StatCard>
          <StatCard label={t("pro_dash.expenses")} icon={TrendingDown} span="md:col-span-6 lg:col-span-4">
            <div className="text-3xl font-mono font-semibold text-red-400 tabular-nums">-{sym}{summary.total_expense.toFixed(2)}</div>
            {summary.velocity && (
              <div className={`text-xs font-mono mt-1 ${summary.velocity.direction === "up" ? "text-red-400" : "brand-text"}`}>
                {summary.velocity.direction === "up" ? "↑" : "↓"} {Math.abs(summary.velocity.pct)}% vs last month
              </div>
            )}
          </StatCard>
        </div>

        {/* Streak + Salary + Recurring + Top Goal */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
          <StreakCard streak={streak} />
          <SalaryCountdown cd={summary.salary_countdown} t={t} currency={currency} />
          <UpcomingRecurring items={summary.upcoming_recurring} currency={currency} />
          <TopGoal goal={summary.top_goal} currency={currency} t={t} reload={load} />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-5">
          <CashFlowChart data={summary.by_month} sym={sym} />
          <CategoryDonut data={summary.by_category} currency={currency} />
        </div>

        {/* Tabs for modules */}
        <div className="mt-8">
          <div className="flex items-center gap-1 border-b border-border mb-6 overflow-x-auto scrollbar-thin">
            {[
              { v: "overview", l: "Overview" },
              { v: "income", l: t("pro_dash.income") },
              { v: "expenses", l: t("pro_dash.expenses") },
              { v: "goals", l: t("pro_dash.savings_goals") },
              { v: "recurring", l: t("pro_dash.recurring") },
              { v: "subscriptions", l: "Subscriptions" },
              { v: "templates", l: "Pre-Set Splits" },
              { v: "ledger", l: t("pro_dash.ledger") },
            ].map((x) => (
              <button
                key={x.v}
                onClick={() => setTab(x.v)}
                className={`px-4 h-10 text-sm transition whitespace-nowrap ${tab === x.v ? "brand-text border-b-2 brand-border" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`pro-tab-${x.v}`}
              >
                {x.l}
              </button>
            ))}
          </div>

          {tab === "overview" && <OverviewFeed items={summary.unified_recent} currency={currency} />}
          {tab === "income" && <IncomeModule incomes={incomes} onChange={load} currency={currency} />}
          {tab === "expenses" && <ExpenseModule expenses={expenses} onChange={load} currency={currency} />}
          {tab === "goals" && <GoalsModule goals={goals} onChange={load} currency={currency} />}
          {tab === "recurring" && <RecurringModule items={recurring} onChange={load} currency={currency} kind="general" />}
          {tab === "subscriptions" && <RecurringModule items={recurring} onChange={load} currency={currency} kind="subscription" />}
          {tab === "templates" && <SplitTemplatesModule />}
          {tab === "ledger" && <LedgerModule items={ledger} currency={currency} />}
        </div>
      </main>
    </div>
  );
}

// ===== Reusable =====
const StatCard = ({ label, icon: Icon, children, span = "", gold = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className={`rounded-2xl border p-6 bg-card/40 ${span}`}
    style={gold ? { borderColor: "rgba(255,215,0,0.4)", background: "linear-gradient(135deg, rgba(255,215,0,0.05), rgba(255,165,0,0.02))" } : { borderColor: "hsl(var(--border))" }}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} style={gold ? { color: "#FFD700" } : {}} />
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
    </div>
    {children}
  </motion.div>
);

const StreakCard = ({ streak }) => {
  const [open, setOpen] = useState(false);
  const cur = streak?.current_streak ?? 0;
  const best = streak?.best_streak ?? 0;
  const days = new Set(streak?.activity_days || []);
  const last14 = [...Array(14)].map((_, idx) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - (13 - idx));
    const key = d.toISOString().slice(0, 10);
    return { key, active: days.has(key), label: d.getUTCDate() };
  });
  const challenges = streak?.weekly_challenges || [];
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="rounded-2xl border border-border bg-card/40 p-6 text-left hover:border-orange-500/50 transition group w-full"
        data-testid="widget-streak"
      >
        <div className="flex items-center gap-2 mb-3">
          <Flame className={`h-3.5 w-3.5 ${cur > 0 ? "text-orange-400" : "text-muted-foreground"}`} strokeWidth={1.5} />
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Streak</div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className={`text-4xl font-mono font-semibold tabular-nums ${cur > 0 ? "text-orange-400" : "text-muted-foreground"}`}>{cur}</span>
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">day{cur === 1 ? "" : "s"}</span>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">Best · {best}d</div>
        <div className="mt-4 grid grid-cols-14 gap-[3px]" style={{ gridTemplateColumns: "repeat(14, minmax(0,1fr))" }}>
          {last14.map((d) => (
            <span
              key={d.key}
              title={d.key}
              className={`h-2 rounded-sm ${d.active ? "bg-orange-400" : "bg-foreground/[0.08]"}`}
            />
          ))}
        </div>
        <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground opacity-0 group-hover:opacity-100 transition">
          Tap for weekly challenges →
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="streak-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-400" strokeWidth={1.75} /> Weekly challenges
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            {challenges.map((c) => {
              const pct = Math.min(100, Math.round((c.progress / c.target) * 100));
              return (
                <div key={c.id} className={`rounded-lg border p-3 ${c.completed ? "border-orange-500/40 bg-orange-500/[0.05]" : "border-border bg-foreground/[0.02]"}`} data-testid={`challenge-${c.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium">{c.title}</div>
                    {c.completed && <Trophy className="h-4 w-4 text-orange-400" strokeWidth={1.75} />}
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">{c.description}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-foreground/[0.08] rounded-full overflow-hidden">
                      <div className={`h-full ${c.completed ? "bg-orange-400" : "brand-bg"} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-mono tabular-nums text-muted-foreground">{c.progress}/{c.target}</span>
                  </div>
                </div>
              );
            })}
            {challenges.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">Come back tomorrow to start logging streaks.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SalaryCountdown = ({ cd, t, currency }) => (
  <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid="widget-salary">
    <div className="flex items-center gap-2 mb-3">
      <CalendarClock className="h-3.5 w-3.5" strokeWidth={1.5} />
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{t("pro_dash.next_salary")}</div>
    </div>
    {!cd ? (
      <div className="text-sm text-muted-foreground py-3">{t("pro_dash.no_data")}</div>
    ) : cd.overdue ? (
      <div>
        <div className="text-2xl font-mono font-semibold text-red-400">{t("pro_dash.overdue")}</div>
        <div className="text-sm text-muted-foreground mt-1">{cd.source} · {formatAmount(cd.amount, currency)}</div>
      </div>
    ) : (
      <div>
        <div className="text-4xl font-mono font-semibold tabular-nums">{cd.days}</div>
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">{t("pro_dash.days_until")} {cd.source} · {formatAmount(cd.amount, currency)}</div>
      </div>
    )}
  </div>
);

const UpcomingRecurring = ({ items, currency }) => (
  <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid="widget-upcoming">
    <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Upcoming</div>
    {items.length === 0 ? (
      <div className="text-sm text-muted-foreground py-3">No recurring bills</div>
    ) : (
      <div className="space-y-2">
        {items.slice(0, 3).map((r) => (
          <div key={r.id} className="flex items-center justify-between text-sm">
            <div className="truncate">{r.title}</div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-mono text-xs text-muted-foreground">{r.days_until != null ? (r.days_until <= 0 ? "due" : `${r.days_until}d`) : "—"}</span>
              <span className="font-mono tabular-nums">{formatAmount(r.amount, currency)}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const TopGoal = ({ goal, currency, t, reload }) => {
  const [amt, setAmt] = useState("");
  const contribute = async () => {
    if (!goal || !amt) return;
    try {
      const { data } = await api.post(`/savings-goals/${goal.id}/contribute`, { amount: Number(amt) });
      setAmt("");
      if (data.completed_at) confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 }, colors: ["#00FF66", "#FFD700", "#FFFFFF"] });
      toast.success("Added to goal");
      reload();
    } catch { toast.error("Failed"); }
  };
  if (!goal) return (
    <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid="widget-goal">
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("pro_dash.savings_goals")}</div>
      <div className="text-sm text-muted-foreground py-3">No goals yet</div>
    </div>
  );
  const pct = Math.min(100, (goal.current_amount / goal.target_amount) * 100);
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid="widget-goal">
      <div className="flex items-center gap-2 mb-3">
        <Target className="h-3.5 w-3.5" strokeWidth={1.5} />
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground truncate">{goal.emoji} {goal.name}</div>
      </div>
      <div className="font-mono text-2xl font-semibold tabular-nums">
        {formatAmount(goal.current_amount, currency)}<span className="text-sm text-muted-foreground"> / {formatAmount(goal.target_amount, currency)}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden mt-3">
        <motion.div className="h-full brand-bg" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
      </div>
      <div className="mt-3 flex gap-2">
        <Input value={amt} onChange={(e) => setAmt(e.target.value)} type="number" placeholder="Add" className="h-9 text-sm bg-muted border-border font-mono" data-testid="goal-add-input" />
        <Button size="sm" onClick={contribute} className="brand-bg text-black h-9" data-testid="goal-add-btn"><Plus className="h-3.5 w-3.5" strokeWidth={2} /></Button>
      </div>
    </div>
  );
};

const CashFlowChart = ({ data, sym }) => (
  <div className="lg:col-span-7 rounded-2xl border border-border bg-card/40 p-6" data-testid="widget-cashflow">
    <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-5">Cash Flow — 6 months</div>
    {Object.keys(data).length === 0 ? (
      <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">No data yet</div>
    ) : (
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={Object.entries(data).slice(-6).map(([month, value]) => ({ month, value }))}>
          <XAxis dataKey="month" stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
          <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={40} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontFamily: "JetBrains Mono" }} />
          <Bar dataKey="value" fill="var(--brand)" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const CategoryDonut = ({ data, currency }) => (
  <div className="lg:col-span-5 rounded-2xl border border-border bg-card/40 p-6" data-testid="widget-cat-donut">
    <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-5">By Category</div>
    {Object.keys(data).length === 0 ? (
      <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">No data</div>
    ) : (
      <div className="flex items-center gap-4">
        <ResponsiveContainer width={140} height={140}>
          <PieChart>
            <Pie data={Object.entries(data).map(([name, value]) => ({ name, value }))} dataKey="value" innerRadius={38} outerRadius={62} paddingAngle={2} strokeWidth={0}>
              {Object.keys(data).map((c) => <Cell key={c} fill={CAT_COLORS[c] || "#8E8E93"} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-1.5 max-h-40 overflow-y-auto scrollbar-thin">
          {Object.entries(data).map(([cat, val]) => {
            const info = ALL_CATS.find((c) => c.value === cat);
            return (
              <div key={cat} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: CAT_COLORS[cat] || "#8E8E93" }} />
                  <span className="capitalize text-muted-foreground">{info?.label || cat}</span>
                </span>
                <span className="font-mono tabular-nums">{formatAmount(val, currency)}</span>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

const OverviewFeed = ({ items, currency }) => (
  <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid="pro-overview-feed">
    <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-5">Recent transactions</div>
    {items.length === 0 ? (
      <div className="text-sm text-muted-foreground py-6 text-center">Add an income or expense to see activity</div>
    ) : (
      <div className="divide-y divide-border">
        {items.map((x) => {
          const cat = ALL_CATS.find((c) => c.value === x.category);
          return (
            <div key={`${x.kind}-${x.id}`} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-9 w-9 rounded-lg bg-foreground/5 flex items-center justify-center">{cat?.emoji || "💸"}</span>
                <div className="min-w-0">
                  <div className="font-medium truncate">{x.title}</div>
                  <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider truncate">{x.source_emoji} {x.source_name}</div>
                </div>
              </div>
              <div className="font-mono tabular-nums">{formatAmount(x.amount, x.currency || currency)}</div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// ===== Modules =====
const IncomeModule = ({ incomes, onChange, currency }) => {
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ source: "", amount: "", frequency: "monthly", expected_day_of_month: "1", is_primary: false });
  const submit = async () => {
    try {
      await api.post("/incomes", { ...form, amount: Number(form.amount), expected_day_of_month: Number(form.expected_day_of_month), currency });
      toast.success("Income added");
      setForm({ source: "", amount: "", frequency: "monthly", expected_day_of_month: "1", is_primary: false });
      setOpen(false); onChange();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/incomes/${id}`); onChange(); };
  return (
    <ModuleShell title="Income sources" onAdd={() => setOpen(true)} addLabel="Add income" testid="module-income">
      {incomes.length === 0 ? <Empty text="No income sources yet" /> : (
        <div className="divide-y divide-border">
          {incomes.map((i) => (
            <button
              key={i.id}
              onClick={() => setEditItem(i)}
              className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-foreground/[0.02] rounded transition -mx-2 px-2"
              data-testid={`income-row-${i.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap"><span className="font-medium truncate">{i.source}</span>{i.is_primary && <span className="text-[10px] font-mono brand-text shrink-0">PRIMARY</span>}</div>
                <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider truncate">{i.frequency}{i.expected_day_of_month ? ` · day ${i.expected_day_of_month}` : ""}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-mono tabular-nums brand-text">+{formatAmount(i.amount, i.currency || currency)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Add income</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <LabeledInput label="Source" value={form.source} onChange={(v) => setForm({ ...form, source: v })} placeholder="Primary salary" tid="income-source" />
            <LabeledInput label="Amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} tid="income-amount" />
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger className="mt-1.5 bg-muted border-border" data-testid="income-freq"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="monthly">Monthly</SelectItem><SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem><SelectItem value="once">One-time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <LabeledInput label="Expected day of month" type="number" value={form.expected_day_of_month} onChange={(v) => setForm({ ...form, expected_day_of_month: v })} tid="income-day" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} className="accent-[var(--brand)]" data-testid="income-primary" />
              Primary income
            </label>
          </div>
          <DialogFooter><Button onClick={submit} className="w-full brand-bg text-black" data-testid="income-submit">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <PROItemEditDrawer
        open={!!editItem}
        onOpenChange={(o) => !o && setEditItem(null)}
        item={editItem}
        type="income"
        currency={currency}
        onSaved={() => { setEditItem(null); onChange(); }}
        onDeleted={() => { setEditItem(null); onChange(); }}
      />
    </ModuleShell>
  );
};

const ExpenseModule = ({ expenses, onChange, currency }) => {
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: "", amount: "", category: "food", note: "" });
  const submit = async () => {
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount), currency });
      toast.success("Expense added");
      setForm({ title: "", amount: "", category: "food", note: "" });
      setOpen(false); onChange();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/expenses/${id}`); onChange(); };
  return (
    <ModuleShell title="Personal expenses" onAdd={() => setOpen(true)} addLabel="Add expense" testid="module-expenses">
      {expenses.length === 0 ? <Empty text="No personal expenses yet" /> : (
        <div className="divide-y divide-border">
          {expenses.map((e) => {
            const cat = ALL_CATS.find((c) => c.value === e.category);
            return (
              <button key={e.id} onClick={() => setEditItem(e)} className="w-full flex items-center justify-between gap-3 py-3 hover:bg-foreground/[0.02] rounded transition -mx-2 px-2 text-left" data-testid={`expense-row-${e.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="h-9 w-9 rounded-lg bg-foreground/5 flex items-center justify-center shrink-0">{cat?.emoji || "💸"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{e.title}</div>
                    <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider truncate">{cat?.label || e.category}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 amount-col">
                  <span className="font-mono tabular-nums text-red-400">-{formatAmount(e.amount, e.currency || currency)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Add expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <LabeledInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} tid="expense-title" />
            <LabeledInput label="Amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} tid="expense-amount" />
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5 bg-muted border-border" data-testid="expense-cat"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {ALL_CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <LabeledInput label="Note" value={form.note} onChange={(v) => setForm({ ...form, note: v })} tid="expense-note" />
          </div>
          <DialogFooter><Button onClick={submit} className="w-full brand-bg text-black" data-testid="expense-submit">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <PROItemEditDrawer
        open={!!editItem}
        onOpenChange={(o) => !o && setEditItem(null)}
        item={editItem}
        type="expense"
        currency={currency}
        onSaved={() => { setEditItem(null); onChange(); }}
        onDeleted={() => { setEditItem(null); onChange(); }}
      />
    </ModuleShell>
  );
};

const GoalsModule = ({ goals, onChange, currency }) => {
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", emoji: "🎯", target_amount: "", target_date: "" });
  const submit = async () => {
    try {
      await api.post("/savings-goals", { ...form, target_amount: Number(form.target_amount), currency });
      toast.success("Goal created");
      setForm({ name: "", emoji: "🎯", target_amount: "", target_date: "" });
      setOpen(false); onChange();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/savings-goals/${id}`); onChange(); };
  const contribute = async (id, amt) => {
    try {
      const { data } = await api.post(`/savings-goals/${id}/contribute`, { amount: amt });
      if (data.completed_at) confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 }, colors: ["#00FF66", "#FFD700", "#FFFFFF"] });
      toast.success(`+${formatAmount(amt, currency)}`);
      onChange();
    } catch { toast.error("Failed"); }
  };
  return (
    <ModuleShell title="Savings goals" onAdd={() => setOpen(true)} addLabel="New goal" testid="module-goals">
      {goals.length === 0 ? <Empty text="Create your first savings goal" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {goals.map((g) => {
            const pct = Math.min(100, (g.current_amount / g.target_amount) * 100);
            const done = !!g.completed_at;
            return (
              <div key={g.id} className="rounded-xl border border-border bg-foreground/[0.02] p-5 cursor-pointer hover:border-[color:var(--brand)]/40 transition" onClick={() => setEditItem(g)} data-testid={`goal-row-${g.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-2xl">{g.emoji}</span>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{g.name}</div>
                      {g.target_date && <div className="text-xs text-muted-foreground font-mono">by {g.target_date.slice(0, 10)}</div>}
                    </div>
                  </div>
                </div>
                <div className="font-mono text-lg font-semibold tabular-nums">
                  {formatAmount(g.current_amount, currency)}<span className="text-sm text-muted-foreground"> / {formatAmount(g.target_amount, currency)}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden mt-3">
                  <motion.div className="h-full" style={{ background: done ? "linear-gradient(90deg, #FFD700, #FFA500)" : "var(--brand)" }}
                    initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs font-mono">
                  <span className="text-muted-foreground">{pct.toFixed(0)}%</span>
                  {done ? <span style={{ color: "#FFD700" }}>COMPLETED ✨</span> : null}
                </div>
                {!done && (
                  <div className="mt-3 flex gap-1" onClick={(e) => e.stopPropagation()}>
                    {[10, 50, 100].map((n) => (
                      <button key={n} onClick={() => contribute(g.id, n)} className="flex-1 h-8 rounded-md border border-border text-xs font-mono hover:bg-foreground/5 transition" data-testid={`goal-contrib-${g.id}-${n}`}>+{currencySymbol(currency)}{n}</button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New goal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <LabeledInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Goa Trip" tid="goal-name" />
            <LabeledInput label="Emoji" value={form.emoji} onChange={(v) => setForm({ ...form, emoji: v })} tid="goal-emoji" />
            <LabeledInput label="Target amount" type="number" value={form.target_amount} onChange={(v) => setForm({ ...form, target_amount: v })} tid="goal-target" />
            <LabeledInput label="Target date" type="date" value={form.target_date} onChange={(v) => setForm({ ...form, target_date: v })} tid="goal-date" />
          </div>
          <DialogFooter><Button onClick={submit} className="w-full brand-bg text-black" data-testid="goal-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <PROItemEditDrawer
        open={!!editItem}
        onOpenChange={(o) => !o && setEditItem(null)}
        item={editItem}
        type="goal"
        currency={currency}
        onSaved={() => { setEditItem(null); onChange(); }}
        onDeleted={() => { setEditItem(null); onChange(); }}
      />
    </ModuleShell>
  );
};

const RecurringModule = ({ items, onChange, currency, kind = "general" }) => {
  const isSub = kind === "subscription";
  const filtered = useMemo(
    () => items.filter((r) => isSub ? r.category === "subscription" : r.category !== "subscription"),
    [items, isSub]
  );
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: "", amount: "", frequency: "monthly", next_due: "", category: isSub ? "subscription" : "general" });

  // Keep the form's default category aligned with the active tab
  useEffect(() => {
    setForm((f) => ({ ...f, category: isSub ? "subscription" : "general" }));
  }, [isSub]);

  const submit = async () => {
    try {
      await api.post("/recurring-bills", { ...form, amount: Number(form.amount), currency, next_due: new Date(form.next_due).toISOString() });
      toast.success(isSub ? "Subscription added" : "Recurring added");
      setForm({ title: "", amount: "", frequency: "monthly", next_due: "", category: isSub ? "subscription" : "general" });
      setOpen(false); onChange();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/recurring-bills/${id}`); onChange(); };
  const monthlyEstimate = useMemo(() => {
    // Roll up to a monthly cost for the in-tab items
    const perMonth = (r) => {
      const a = Number(r.amount) || 0;
      switch (r.frequency) {
        case "daily": return a * 30;
        case "weekly": return a * 4.333;
        case "yearly": return a / 12;
        case "monthly":
        default: return a;
      }
    };
    return filtered.reduce((s, r) => s + perMonth(r), 0);
  }, [filtered]);

  return (
    <ModuleShell
      title={isSub ? "Subscriptions" : "Recurring bills"}
      onAdd={() => setOpen(true)}
      addLabel={isSub ? "Add subscription" : "Add recurring"}
      testid={isSub ? "module-subscriptions" : "module-recurring"}
    >
      {filtered.length > 0 && (
        <div className="mb-4 flex items-baseline justify-between gap-3 flex-wrap rounded-lg border border-border bg-foreground/[0.02] px-3 py-2.5">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
            Est. monthly {isSub ? "subscriptions" : "recurring"}
          </span>
          <span className="font-mono tabular-nums brand-text">{formatAmount(monthlyEstimate, currency)}</span>
        </div>
      )}
      {filtered.length === 0 ? (
        <Empty text={isSub ? "Add Netflix, Spotify, Gym, iCloud…" : "Add rent, utilities, EMI, internet…"} />
      ) : (
        <div className="divide-y divide-border">
          {filtered.map((r) => (
            <button key={r.id} onClick={() => setEditItem(r)} className="w-full flex items-center justify-between gap-3 py-3 hover:bg-foreground/[0.02] rounded transition -mx-2 px-2 text-left" data-testid={`recurring-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate flex items-center gap-2">
                  {r.title}
                  {r.paused && <span className="text-[9px] font-mono uppercase tracking-wider text-amber-400 shrink-0">PAUSED</span>}
                </div>
                <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider truncate">{r.frequency} · next {String(r.next_due).slice(0, 10)}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0 amount-col">
                <span className="font-mono tabular-nums">{formatAmount(r.amount, r.currency || currency)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{isSub ? "Add subscription" : "Add recurring"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <LabeledInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder={isSub ? "Netflix" : "Rent · Apt 4B"} tid="rec-title" />
            <LabeledInput label="Amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} tid="rec-amount" />
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger className="mt-1.5 bg-muted border-border" data-testid="rec-freq"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem><SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <LabeledInput label="Next due" type="date" value={form.next_due} onChange={(v) => setForm({ ...form, next_due: v })} tid="rec-date" />
          </div>
          <DialogFooter><Button onClick={submit} className="w-full brand-bg text-black" data-testid="rec-submit">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <PROItemEditDrawer
        open={!!editItem}
        onOpenChange={(o) => !o && setEditItem(null)}
        item={editItem}
        type={isSub ? "subscription" : "recurring"}
        currency={currency}
        onSaved={() => { setEditItem(null); onChange(); }}
        onDeleted={() => { setEditItem(null); onChange(); }}
      />
    </ModuleShell>
  );
};

const LedgerModule = ({ items, currency }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return items.filter((i) => !s || i.from_name.toLowerCase().includes(s) || i.to_name.toLowerCase().includes(s) || i.group_name.toLowerCase().includes(s));
  }, [items, q]);

  const exportCsv = () => {
    const header = ["date", "from", "to", "amount", "currency", "group", "method", "proof_url", "disputed"];
    const rows = filtered.map((i) => [
      i.created_at, i.from_name, i.to_name, i.amount, i.currency, i.group_name,
      i.method || "", i.proof_url || "", i.disputed ? "true" : "false",
    ]);
    const csv = [header.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payments-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid="module-ledger">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Unified payments ledger</div>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search person or group" className="h-9 w-56 bg-muted border-border text-sm" data-testid="ledger-search" />
          <Button onClick={exportCsv} variant="outline" className="border-border h-9 text-xs" data-testid="ledger-export-csv">
            <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> CSV
          </Button>
        </div>
      </div>
      {filtered.length === 0 ? <Empty text="No payments yet" /> : (
        <div className="divide-y divide-border">
          {filtered.map((i) => (
            <div key={i.id} className="flex items-center justify-between py-3" data-testid={`ledger-row-${i.id}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex items-center gap-1 shrink-0">
                  <span className="h-7 w-7 rounded-full bg-foreground/5 flex items-center justify-center text-xs">{i.from_avatar}</span>
                  <span className="text-muted-foreground text-xs">→</span>
                  <span className="h-7 w-7 rounded-full bg-foreground/5 flex items-center justify-center text-xs">{i.to_avatar}</span>
                </div>
                <div className="min-w-0">
                  <div className="text-sm truncate">{i.from_name} → {i.to_name}</div>
                  <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider">{i.group_emoji} {i.group_name} · {formatDistanceToNowStrict(new Date(i.created_at), { addSuffix: true })}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {i.disputed && <span className="text-[10px] font-mono uppercase text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" strokeWidth={1.5} />DISPUTED</span>}
                <span className="font-mono tabular-nums">{formatAmount(i.amount, i.currency || currency)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Tiny helpers =====
const ModuleShell = ({ title, onAdd, addLabel, children, testid }) => (
  <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-5">
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{title}</div>
      <Button onClick={onAdd} size="sm" className="brand-bg text-black hover:opacity-90 h-8" data-testid={`${testid}-add`}>
        <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> {addLabel}
      </Button>
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => <div className="py-8 text-center text-sm text-muted-foreground">{text}</div>;

const LabeledInput = ({ label, value, onChange, type = "text", placeholder, tid }) => (
  <div>
    <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1.5 bg-muted border-border" data-testid={tid} />
  </div>
);
